import type {MatrixClient, MatrixEvent} from 'matrix-js-sdk';
import {RING_TO_DEVICE_EVENT, type CallIntent, type DeliveredRing, type RingDelivery} from '@phonehq/rtc-core';
import type {TargetedRing} from './groupCallOrchestrator';

interface RingToDeviceContent {
    readonly ring_id: string;
    readonly room_id: string;
    readonly intent: CallIntent;
    readonly notification_event_id: string;
    readonly expires_ts: number;
}

export const ringToDeviceContent = (ring: DeliveredRing): RingToDeviceContent => ({
    ring_id: ring.ringId,
    room_id: ring.roomId,
    intent: ring.intent,
    notification_event_id: ring.notificationEventId,
    expires_ts: Date.parse(ring.expiresAt),
});

export const createToDeviceRingDelivery = (client: MatrixClient): RingDelivery => ({
    deliver: async (targets, ring) => {
        const crypto = client.getCrypto();
        if (!crypto || targets.length === 0) {
            return;
        }
        const deviceMap = await crypto.getUserDeviceInfo([...targets], true);
        const devices = targets.flatMap(userId => [...(deviceMap.get(userId)?.keys() ?? [])].map(deviceId => ({userId, deviceId})));
        if (devices.length === 0) {
            return;
        }
        await client.encryptAndSendToDevice(RING_TO_DEVICE_EVENT, devices, {...ringToDeviceContent(ring)});
    },
});

const isString = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const parseTargetedRing = (event: Pick<MatrixEvent, 'getType' | 'getContent' | 'getSender' | 'isEncrypted'>): TargetedRing | undefined => {
    if (event.getType() !== RING_TO_DEVICE_EVENT || !event.isEncrypted()) {
        return undefined;
    }
    const content = event.getContent() as Partial<Record<keyof RingToDeviceContent, unknown>>;
    const sender = event.getSender();
    const {ring_id: ringId, room_id: roomId, notification_event_id: notificationEventId, expires_ts: expiresAt, intent} = content;
    if (
        !isString(sender) || !isString(ringId) || !UUID.test(ringId) || !isString(roomId) || !roomId.startsWith('!') ||
        !isString(notificationEventId) || typeof expiresAt !== 'number' || !Number.isFinite(expiresAt)
    ) {
        return undefined;
    }
    return {
        ringId,
        roomId,
        sender,
        notificationEventId,
        expiresAt,
        intent: intent === 'video' ? 'video' : 'audio',
    };
};
